import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type SyntheticEvent,
} from "react";
import {
  type GridFilterCellProps,
  type GridCustomFilterCellProps,
} from "@progress/kendo-react-grid";
import {
  NumericTextBox,
  type NumericTextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { filterClearIcon } from "@progress/kendo-svg-icons";
import { HeaderTdElement } from "@progress/kendo-react-data-tools";

function getColumnName(props: GridFilterCellProps): string {
  return props.title || props.field || "column";
}

export function NumericFilterCell(
  props: GridCustomFilterCellProps,
  title: string
) {
  return (
    <HeaderTdElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
    >
      <NumericFilterCellInner {...props} title={title} />
    </HeaderTdElement>
  );
}

const NumericFilterCellInner = (props: GridFilterCellProps) => {
  const columnName = getColumnName(props);
  const hasValue =
    props.value !== null && props.value !== undefined && props.value !== "";

  const onChange = (event: NumericTextBoxChangeEvent) => {
    const value = event.value;
    const isSet = value !== null && value !== undefined;
    props.onChange({
      value: isSet ? value : "",
      operator: isSet ? "eq" : "",
      syntheticEvent: event.syntheticEvent,
    });
  };

  const onClear = (event: SyntheticEvent) => {
    event.preventDefault();
    props.onChange({ value: "", operator: "", syntheticEvent: event });
  };

  return (
    <div className="k-filtercell">
      <div className="k-filtercell-wrapper">
        <NumericTextBox
          value={hasValue ? (props.value as number) : null}
          onChange={onChange}
          fillMode={"outline"}
          ariaLabel={`Filter by ${columnName}`}
        />
        <Button
          // aria-label alone is enough; a title attribute alongside it
          // becomes the accessible description, and NVDA reads that too -
          // "Clear Record Type filter, button, Clear" - a redundant
          // trailing "Clear" on every clear button in this file.
          aria-label={`Clear ${columnName} filter`}
          disabled={!hasValue}
          onClick={onClear}
          svgIcon={filterClearIcon}
        />
      </div>
    </div>
  );
};

export function DateFilterCell(
  props: GridCustomFilterCellProps,
  title: string
) {
  return (
    <HeaderTdElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
    >
      <DateFilterCellInner {...props} title={title} />
    </HeaderTdElement>
  );
}

function toDateInputValue(value: unknown): string {
  if (!value) return "";
  const date = new Date(value as string | number | Date);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const DATE_FILTER_COMMIT_DELAY_MS = 500;

const DateFilterCellInner = (props: GridFilterCellProps) => {
  const columnName = getColumnName(props);
  const hasValue =
    props.value !== null && props.value !== undefined && props.value !== "";

  const commitTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => clearTimeout(commitTimeoutRef.current);
  }, []);

  const [resetKey, setResetKey] = useState(0);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    clearTimeout(commitTimeoutRef.current);
    const raw = event.target.value;
    if (!raw) return;
    commitTimeoutRef.current = setTimeout(() => {
      props.onChange({
        value: new Date(`${raw}T00:00:00`),
        operator: "eq",
        syntheticEvent: event,
      });
    }, DATE_FILTER_COMMIT_DELAY_MS);
  };

  const onClear = (event: SyntheticEvent) => {
    clearTimeout(commitTimeoutRef.current);
    event.preventDefault();
    setResetKey((key) => key + 1);
    props.onChange({ value: "", operator: "", syntheticEvent: event });
  };

  return (
    <div className="k-filtercell">
      <div className="k-filtercell-wrapper">
        <input
          key={resetKey}
          type="date"
          className="form-control"
          max="9999-12-31"
          defaultValue={toDateInputValue(props.value)}
          onChange={onChange}
          aria-label={`Filter by ${columnName}`}
        />
        <Button
          aria-label={`Clear ${columnName} filter`}
          disabled={!hasValue}
          onClick={onClear}
          svgIcon={filterClearIcon}
        />
      </div>
    </div>
  );
};
